import React, { useEffect, useMemo } from 'react'
import ClusterPicklist from 'k8s/components/common/ClusterPicklist'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import { listTablePrefs, UserPreferences, TablePrefsParams } from 'app/constants'
import { pathOr, pick } from 'ramda'
import { DateAndTime } from 'core/components/listTable/cells/DateCell'
import { routes } from 'core/utils/routes'
import SetAsDefaultDialog from '../../../infrastructure/components/clusters/SetAsDefaultDialog'
import { makeStyles } from '@material-ui/styles'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { emptyObj } from 'utils/fp'
import Text from 'core/elements/Text'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import { renderLabelsAsBadges } from '../common/entity/labels-and-annotations/helpers'
import { getNamespaceStatus } from './helpers'
import DeleteNamespaceDialog from './delete-dialog'
import { isDeccoEnv } from 'core/utils/helpers'
import DocumentMeta from 'core/components/DocumentMeta'
import Tooltip from 'core/elements/tooltip'
import DataKeys from 'k8s/DataKeys'
import { ArrayElement } from 'core/actions/Action'
import { GridViewColumn } from 'core/elements/grid/Grid'
import { GridFilterSpec } from 'core/elements/grid/hooks/useGridFiltering'
import ListContainer from 'core/containers/ListContainer'
import useListAction from 'core/hooks/useListAction'
import useSelectorWithParams from 'core/hooks/useSelectorWithParams'
import { listNamespaces, deleteNamespace } from './new-actions'
import Theme from 'core/themes/model'
import { namespacesSelector } from './selectors'
import getGridDialogButton from 'core/elements/grid/helpers/getGridDialogButton'
import AddNamespaceForm from 'k8s/components/namespaces/AddNamespacePage'
import { createGridStatusCell } from 'core/elements/grid/cells/GridStatusCell'
import InferActionParams from 'core/actions/InferActionParams'
import useGlobalParams from 'core/hooks/useGlobalParams'
import { useAppSelector } from 'app/store'

type ModelDataKey = DataKeys.Namespaces
type SelectorModel = ArrayElement<ReturnType<typeof namespacesSelector>>
type ActionParams = InferActionParams<typeof listNamespaces>
// @fixme using a type here because of https://github.com/microsoft/TypeScript/issues/15300
type Params = ActionParams & {
  masterNodeClusters: boolean
  healthyClusters: boolean
}

const requiredParams: Array<keyof ActionParams> = ['clusterId']
const defaultParams: Params = {
  clusterId: null,
  masterNodeClusters: true,
  healthyClusters: true,
}

const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>(
  'Namespaces',
  listTablePrefs,
)

const canDeleteNameSpace = ([namespace]) => namespace?.status !== 'Terminating'

const useStyles = makeStyles((theme: Theme) => ({
  starIcon: {
    color: theme.palette.yellow.main,
    fontSize: theme.typography.body2.fontSize,
    fontWeight: 400,
  },
  namespaceName: {
    display: 'inline-grid',
    gridAutoColumns: 'minmax(min-content, max-content)',
    gridAutoFlow: 'column',
    alignItems: 'center',
    gap: 8,
  },
}))

const NamespaceName = ({ item: namespace }) => {
  const classes = useStyles()
  const { prefs, getUserPrefs } = useScopedPreferences('defaults')
  const namespaceDefaults = prefs[UserPreferences.Namespace] || emptyObj
  const defaultNamespace = pathOr('', [namespace.clusterId, 'namespace'], namespaceDefaults)
  const isDefaultNamespace = namespace.name === defaultNamespace

  useEffect(() => {
    getUserPrefs(UserPreferences.Cluster)
  }, [])

  return (
    <div className={classes.namespaceName}>
      {isDefaultNamespace && (
        <Tooltip message="Default Namespace">
          <FontAwesomeIcon className={classes.starIcon}>star</FontAwesomeIcon>
        </Tooltip>
      )}
      <Text variant="body2">{namespace.name}</Text>
    </div>
  )
}

const searchTargets = ['name', 'clusterName']

const columns: GridViewColumn<SelectorModel>[] = [
  // Disable cell memoization since it prevents the hook within the cell to fetch the updated user prefs
  { key: 'name', label: 'Name', width: 'medium', CellComponent: NamespaceName, memoizeCell: false },
  {
    key: 'status.phase',
    label: 'Status',
    CellComponent: createGridStatusCell({
      dataFn: getNamespaceStatus,
    }),
  },
  {
    key: 'spec.finalizers',
    label: 'Finalizers',
    render: renderLabelsAsBadges({ variant: 'default', defaultValue: 'No Finalizers' }),
  },
  { key: 'clusterName', label: 'Cluster', width: 'medium' },
  { key: 'created', label: 'Created', CellComponent: DateAndTime },
]

export default function NamespacesListPage() {
  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)
  const { message, loading, reload } = useListAction(listNamespaces, {
    params,
    requiredParams,
  })
  const data = useAppSelector(namespacesSelector)

  const batchActions = useMemo(
    () => [
      {
        cond: (selected) => selected.length === 1,
        icon: 'star',
        label: 'Set as Default',
        BatchActionButton: getGridDialogButton(SetAsDefaultDialog, {
          defaultsKey: UserPreferences.Namespace,
          type: 'namespace',
        }),
      },
    ],
    [],
  )
  const { fetchUserDefaults } = useScopedPreferences('defaults')

  useEffect(() => {
    if (isDeccoEnv) {
      fetchUserDefaults(UserPreferences.Namespace)
    }
  }, [])

  return (
    <>
      <DocumentMeta title="Namespaces" />
      <AddNamespaceForm />
      <ListContainer<ModelDataKey, SelectorModel>
        dataKey={DataKeys.Namespaces}
        searchTargets={searchTargets}
        uniqueIdentifier="id"
        loading={loading}
        loadingMessage={message}
        onRefresh={reload}
        data={data}
        columns={columns}
        addUrl={routes.namespaces.add.path()}
        addText="Add Namespace"
        getParamsUpdater={getParamsUpdater}
        deleteAction={deleteNamespace}
        deleteCond={canDeleteNameSpace}
        DeleteDialogComponent={DeleteNamespaceDialog}
        batchActions={batchActions}
        {...pick(listTablePrefs, params)}
      />
    </>
  )
}
